import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { chatKeys } from "../../features/chat";
import type { InboxItem } from "../../features/chat/types";
import { ARCHIVED_INBOX_REQUEST, TEAM_INBOX_REQUEST } from "./constants";

export type InboxCachePatch = Partial<
  Pick<InboxItem, "sentTime" | "displayInfo3" | "lastMessage" | "unread">
>;

export type InboxCachePatchOptions = {
  /** Remove the chatroom from the list entirely (e.g. moved to archived) */
  remove?: boolean;
  /** Move the chatroom to the top of the list (new/latest message). Default true. */
  moveToFront?: boolean;
};

/**
 * Patches the cached team/archived inbox lists directly (move thread to top,
 * update preview/unread) instead of refetching from the server — mirrors
 * mobile's local thread update (CommunicationsHome.tsx) with no REST call.
 *
 * Returns `patchInboxCache(chatroomId, updates, options?)` → boolean
 * indicating whether the chatroom was found (and patched) in either cache.
 * The patch is durable in the cache — unlike a component-state override, it
 * survives the owning component unmounting (e.g. navigating to another route).
 */
export function useInboxCachePatcher() {
  const queryClient = useQueryClient();

  return useCallback(
    (
      chatroomId: string,
      updates: InboxCachePatch,
      { remove = false, moveToFront = true }: InboxCachePatchOptions = {},
    ) => {
      const patchList = (queryKey: readonly unknown[]) => {
        const current = queryClient.getQueryData<InboxItem[]>(queryKey);
        if (!current) return false;
        const index = current.findIndex(
          (item) => String(item.chatroomId) === chatroomId,
        );
        if (index === -1) return false;
        const updated = [...current];
        if (remove) {
          updated.splice(index, 1);
          queryClient.setQueryData(queryKey, updated);
          return true;
        }
        if (moveToFront) {
          const [moved] = updated.splice(index, 1);
          updated.unshift({ ...moved, ...updates });
        } else {
          updated[index] = { ...updated[index], ...updates };
        }
        queryClient.setQueryData(queryKey, updated);
        return true;
      };

      const patchedTeam = patchList(chatKeys.inbox(TEAM_INBOX_REQUEST));
      const patchedArchived = patchList(chatKeys.inbox(ARCHIVED_INBOX_REQUEST));
      return patchedTeam || patchedArchived;
    },
    [queryClient],
  );
}
